import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

export const REPORT_AVAILABLE_STATUS = "AVAILABLE";

export const REPORT_ORDER_STATUS_OPTIONS = [
  { label: "Tất cả đơn hàng", value: "ALL" },
  { label: "Đơn đã hoàn thành", value: "FINISHED" },
];

export type ReportConfig = {
  code: string;
  name: string;
  urlPath: string;
  filterKeys: string[];
  status: string;
};

export type ReportPeriodMode = "month" | "quarter" | "year";

export type ReportPeriodOption = {
  label: string;
  value: number;
};

export type ReportOption = {
  label: string;
  value: string | number;
  _id?: string;
  vehicleList?: any[];
  [key: string]: any;
};

type BuildReportExportParamsInput = {
  filterKeys: string[];
  values: Record<string, any>;
};

export const isReportAvailable = (report?: Pick<ReportConfig, "status"> | null) =>
  report?.status === REPORT_AVAILABLE_STATUS;

export const getFirstAvailableReport = (reports: ReportConfig[]) =>
  reports.find((report) => isReportAvailable(report));

export const hasReportFilter = (
  report: Pick<ReportConfig, "filterKeys"> | undefined,
  filterKey: string,
) => Boolean(report?.filterKeys?.includes(filterKey));

const isDayjsValue = (value: unknown): value is Dayjs => dayjs.isDayjs(value);

export const normalizeReportOptionValue = (value: any): any => {
  if (value === undefined || value === null || value === "") return undefined;

  if (Array.isArray(value)) {
    const normalizedValues = value
      .map((item) => normalizeReportOptionValue(item))
      .filter((item) => item !== undefined && item !== null && item !== "");

    return normalizedValues.length ? normalizedValues.join(",") : undefined;
  }

  if (isDayjsValue(value)) {
    return value.toISOString();
  }

  if (typeof value === "object") {
    return value._id ?? value.value ?? value.key;
  }

  return value;
};

export const buildReportExportParams = ({
  filterKeys,
  values,
}: BuildReportExportParamsInput) => {
  return filterKeys.reduce<Record<string, any>>((params, key) => {
    const rawValue = values[key];
    let value = normalizeReportOptionValue(rawValue);

    if (isDayjsValue(rawValue)) {
      if (key === "from") value = rawValue.startOf("day").toISOString();
      if (key === "to") value = rawValue.endOf("day").toISOString();
      if (key === "orderStagePeriod") {
        value = rawValue.format("YYYY-MM");
      }
    }

    if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }

    return params;
  }, {});
};

const normalizeVehicleOption = (vehicle: any): ReportOption | null => {
  if (!vehicle) return null;

  if (typeof vehicle === "string" || typeof vehicle === "number") {
    return {
      label: String(vehicle),
      value: String(vehicle),
    };
  }

  const value = vehicle._id ?? vehicle.value ?? vehicle.licensePlate;
  const label = vehicle.label ?? vehicle.licensePlate ?? vehicle.name ?? value;

  if (!value || !label) return null;

  return {
    label: String(label),
    value,
  };
};

export const getVehicleOptionsFromTruckVendor = (truckVendor?: any) =>
  (truckVendor?.vehicleList ?? [])
    .map(normalizeVehicleOption)
    .filter(Boolean) as ReportOption[];

export const shouldDisableReportTruckVendor = (
  filterKeys: string[],
  values: Record<string, any>,
) => filterKeys.includes("ownership") && !values.ownership;

export const mapPermissionRegionsToTreeData = (regions: any[]): any[] =>
  (regions ?? []).map((region: any) => ({
    ...region,
    title: region.title ?? region.name,
    value: region._id,
    key: region.key ?? region.code ?? region._id,
    children: mapPermissionRegionsToTreeData(region.children ?? []),
  }));

export const getReportPeriodOptions = (base = dayjs()) => {
  const currentYear = base.year();

  return {
    month: Array.from({ length: 12 }, (_, index) => ({
      label: `Tháng ${index + 1}`,
      value: index,
    })),
    quarter: Array.from({ length: 4 }, (_, index) => ({
      label: `Quý ${index + 1}`,
      value: index + 1,
    })),
    year: Array.from({ length: 6 }, (_, index) => {
      const year = currentYear - index;

      return {
        label: `Năm ${year}`,
        value: year,
      };
    }),
  };
};

export const getReportPeriodRange = (
  mode: ReportPeriodMode,
  value: number,
  base = dayjs(),
): [Dayjs, Dayjs] => {
  if (mode === "quarter") {
    const startMonth = (value - 1) * 3;
    const start = base.month(startMonth).startOf("month");

    return [start, start.add(2, "month").endOf("month")];
  }

  if (mode === "year") {
    const start = dayjs(`${value}-01-01`).startOf("year");

    return [start, start.endOf("year")];
  }

  const start = base.month(value).startOf("month");
  return [start, start.endOf("month")];
};
